/**
 * The /app/patientprofile/:patientID route component (Patient Profile overhaul, 2026-10-08).
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * Renders EXACTLY ONE page:
 *   PatientProfilePage.vue — the new page (design 1a, built on src/components/PatientPanel/)
 *   PatientProfile.vue     — the classic page, NOT edited
 * decided by shouldUseNewFollowupsUi('patientProfileLayout', …): Super Admins get the new page unless
 * they picked "Classic view" (localStorage teamio_patient_profile_ui = 'old'); everyone else follows
 * STAGE.patientProfileLayout and their one office choice (teamio_ui_choice:<employeeID>) —
 * uiVersion.js. The classic-side link hides itself for an office user unless the stage is
 * 'invite' / 'default' (FollowupsUiToggle).
 *
 * ── WHY A .js RENDER FUNCTION, NOT A .vue WRAPPER ───────────────────────────────────────────────
 * Each page mounts its own <slideout-panel> outlet, as every route page does. Exactly one page is
 * ever rendered here, so exactly one outlet is ever alive — the invariant
 * cypress/node-tests/slideout-outlet-guard.test.js protects. That guard identifies a page ROOT by
 * "no other .vue imports it"; a .vue wrapper would make both pages look embedded (and PatientProfile.vue
 * cannot take the guard's `v-if` idiom without editing it). A route component in .js keeps both as
 * the page roots they really are.
 */
import PatientProfile from './PatientProfile.vue';
import PatientProfilePage from './PatientProfilePage.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';

export default {
  name: 'PatientProfileRoute',

  props: {
    patientID: { type: String, required: true },
  },

  data() {
    // Bumped on every Classic / New click so the gate re-reads storage.
    return { layoutVersion: 0 };
  },

  computed: {
    isSuperAdminUser() {
      const user = this.$store.state.customer && this.$store.state.customer.user;
      return !!(user && user.Role === 'Super Admin');
    },
    // The employee ID the office choice is stored under (a route component has no mixin).
    officeUserKey() {
      return this.$store.state.customer ? this.$store.state.customer.userID : null;
    },
    useNew() {
      // eslint-disable-next-line no-unused-expressions
      this.layoutVersion;
      return shouldUseNewFollowupsUi('patientProfileLayout', { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey, storage: browserStorage() });
    },
  },

  methods: {
    // A Super Admin re-renders in place; an office user's switch reloads the page (uiSwitch.js).
    setLayout(target) {
      const how = switchUi('patientProfileLayout', target, { isSuperAdmin: this.isSuperAdminUser, userKey: this.officeUserKey });
      if (how === 'rerender') this.layoutVersion += 1;
    },
  },

  render(h) {
    if (this.useNew) {
      return h(PatientProfilePage, {
        key: `new-${this.patientID}`,
        props: { patientID: this.patientID },
        on: { 'switch-layout': this.setLayout },
      });
    }
    const children = [];
    // The way to the new page from the classic one. FollowupsUiToggle decides by role: always shown to
    // a Super Admin, shown to an office user only while the area is at 'invite' / 'default'.
    children.push(h(FollowupsUiToggle, {
      key: 'to-new',
      style: { marginBottom: '10px' },
      props: { target: 'new', pageKey: 'patientProfileLayout', noteText: 'Super Admin: a redesigned Patient Profile is ready to check.' },
      on: { toggle: this.setLayout },
    }));
    children.push(h(PatientProfile, { key: `old-${this.patientID}`, props: { patientID: this.patientID } }));
    return h('div', { attrs: { 'data-test': 'patient-profile-classic' } }, children);
  },
};
